import {
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  FormField,
  Header,
  Multiselect,
  SpaceBetween,
  StatusIndicator,
  Table,
  TableProps
} from '@amzn/awsui-components-core';
import Input from '@amzn/awsui-components-core/input';
import { FormikProps, getIn } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { ConfirmationModal, ShowModalInfo } from 'src/components/generic-components/ConfirmationModal';
import {
  labelInputToLabelDropdownOptions,
  labelInputToMultiSelectDropdown,
  multiSelectDropdownToLabel
} from 'src/components/generic-components/LabelDropdownUtility';
import { v4 as uuidv4 } from 'uuid';
import { ApplicationFormValues, NonProductionEnvironmentsEntity } from '../../AdminModels';
import { isNonEmbeddableQuickSightUrl } from './ApplicationLinksConfig';

interface LinksAndTagsProps {
  formik: FormikProps<ApplicationFormValues>;
}

export const ApplicationTags: React.FC<LinksAndTagsProps> = ({ formik }) => {
  const appContext = useAppContext();

  return (
    <ColumnLayout columns={3}>
      <FormField
        label={
          <>
            Lowest Time Grain<i> - optional</i>
          </>
        }
        errorText={getIn(formik.touched, 'linksAndTags.timeGranularity') && getIn(formik.errors, 'linksAndTags.timeGranularity')}
      >
        <Multiselect
          placeholder="Choose options"
          selectedOptions={labelInputToMultiSelectDropdown(formik.values.linksAndTags?.timeGranularity)}
          onChange={({ detail }) => {
            formik.setFieldValue('linksAndTags.timeGranularity', multiSelectDropdownToLabel(detail, 'TimeGranularity'));
          }}
          onBlur={() => {
            formik.setFieldTouched('linksAndTags.timeGranularity', true);
          }}
          deselectAriaLabel={(e) => `Remove ${e.label}`}
          options={labelInputToLabelDropdownOptions(appContext.listApplicationDropdowns?.listTimeGranularity)}
          filteringType="auto"
          selectedAriaLabel="Selected"
        />
      </FormField>

      <FormField
        label={
          <>
            Key Dimension<i> - optional</i>
          </>
        }
        errorText={getIn(formik.touched, 'linksAndTags.keyDimension') && getIn(formik.errors, 'linksAndTags.keyDimension')}
      >
        <Multiselect
          placeholder="Choose options"
          selectedOptions={labelInputToMultiSelectDropdown(formik.values.linksAndTags?.keyDimension)}
          onChange={({ detail }) => {
            formik.setFieldValue('linksAndTags.keyDimension', multiSelectDropdownToLabel(detail, 'KeyDimension'));
          }}
          onBlur={() => {
            formik.setFieldTouched('linksAndTags.keyDimension', true);
          }}
          deselectAriaLabel={(e) => `Remove ${e.label}`}
          options={labelInputToLabelDropdownOptions(appContext.listApplicationDropdowns?.listKeyDimension)}
          filteringType="auto"
          selectedAriaLabel="Selected"
        />
      </FormField>

      <FormField
        label={
          <>
            Last Enhanced<i> - optional</i>
          </>
        }
        errorText={getIn(formik.touched, 'linksAndTags.lastEnhanced') && getIn(formik.errors, 'linksAndTags.lastEnhanced')}
      >
        <Multiselect
          placeholder="Choose options"
          selectedOptions={labelInputToMultiSelectDropdown(formik.values.linksAndTags?.lastEnhanced)}
          onChange={({ detail }) => {
            formik.setFieldValue('linksAndTags.lastEnhanced', multiSelectDropdownToLabel(detail, 'LastEnhanced'));
          }}
          onBlur={() => {
            formik.setFieldTouched('linksAndTags.lastEnhanced', true);
          }}
          deselectAriaLabel={(e) => `Remove ${e.label}`}
          options={labelInputToLabelDropdownOptions(appContext.listApplicationDropdowns?.listLastEnhanced)}
          filteringType="auto"
          selectedAriaLabel="Selected"
        />
      </FormField>

      <FormField
        label={
          <>
            Finance Cycle<i> - optional</i>
          </>
        }
        errorText={getIn(formik.touched, 'linksAndTags.process') && getIn(formik.errors, 'linksAndTags.process')}
      >
        <Multiselect
          placeholder="Choose options"
          selectedOptions={labelInputToMultiSelectDropdown(formik.values.linksAndTags?.process)}
          onChange={({ detail }) => {
            formik.setFieldValue('linksAndTags.process', multiSelectDropdownToLabel(detail, 'Process'));
          }}
          onBlur={() => {
            formik.setFieldTouched('linksAndTags.process', true);
          }}
          deselectAriaLabel={(e) => `Remove ${e.label}`}
          options={labelInputToLabelDropdownOptions(appContext.listApplicationDropdowns?.listProcess)}
          filteringType="auto"
          selectedAriaLabel="Selected"
        />
      </FormField>

      <FormField
        label={
          <>
            Scenario<i> - optional</i>
          </>
        }
        errorText={getIn(formik.touched, 'linksAndTags.scenario') && getIn(formik.errors, 'linksAndTags.scenario')}
      >
        <Multiselect
          placeholder="Choose options"
          selectedOptions={labelInputToMultiSelectDropdown(formik.values.linksAndTags?.scenario)}
          onChange={({ detail }) => {
            formik.setFieldValue('linksAndTags.scenario', multiSelectDropdownToLabel(detail, 'Scenario'));
          }}
          onBlur={() => {
            formik.setFieldTouched('linksAndTags.scenario', true);
          }}
          deselectAriaLabel={(e) => `Remove ${e.label}`}
          options={labelInputToLabelDropdownOptions(appContext.listApplicationDropdowns?.listScenario)}
          filteringType="auto"
          selectedAriaLabel="Selected"
        />
      </FormField>
    </ColumnLayout>
  );
};

const EMPTY_ENV: NonProductionEnvironmentsEntity = {
  id: null,
  // accessLevel retained for API backward compatibility — will be cleaned up in a wider refactor.
  accessLevel: { id: '', label: '', labelType: 'AccessLevel' },
  displayText: '',
  link: ''
};

interface NonProdCellProps {
  item: NonProductionEnvironmentsEntity;
  onUpdate: (id: string, field: keyof NonProductionEnvironmentsEntity, value: string) => void;
}

interface LinkCellProps extends NonProdCellProps {
  showEmbedWarning: boolean;
}

const DisplayNameCell = React.memo<NonProdCellProps>(({ item, onUpdate }) => (
  <Input value={item.displayText || ''} onChange={({ detail }) => onUpdate(item.id!, 'displayText', detail.value)} placeholder="e.g., Alpha, Beta" />
));

const LinkCell = React.memo<LinkCellProps>(({ item, onUpdate, showEmbedWarning }) => (
  <SpaceBetween size="xxs" direction="vertical">
    <Input value={item.link || ''} onChange={({ detail }) => onUpdate(item.id!, 'link', detail.value)} placeholder="https://..." />
    {showEmbedWarning && isNonEmbeddableQuickSightUrl(item.link) && (
      <StatusIndicator type="warning">Console URL — use an embed URL for embedding</StatusIndicator>
    )}
  </SpaceBetween>
));

interface NonProdEnvConfigProps extends LinksAndTagsProps {
  showEmbedWarnings: boolean;
}

export const NonProductionEnvironmentsConfig: React.FC<NonProdEnvConfigProps> = ({ formik, showEmbedWarnings }) => {
  const formikRef = useRef(formik);
  formikRef.current = formik;

  const [showModalInfo, setShowModalInfo] = useState<ShowModalInfo>({
    showModal: false,
    eventHeader: '',
    eventContentType: ''
  });

  const [nonProductionEnvironments, setNonProductionEnvironments] = useState<NonProductionEnvironmentsEntity[]>(
    formik.values.linksAndTags?.nonProductionEnvironments || []
  );
  const [selectedItems, setSelectedItems] = useState<NonProductionEnvironmentsEntity[]>([]);

  useEffect(() => {
    setNonProductionEnvironments(formik.values.linksAndTags?.nonProductionEnvironments || []);
  }, [formik.values.linksAndTags?.nonProductionEnvironments]);

  const syncToFormik = (updated: NonProductionEnvironmentsEntity[]) => {
    setNonProductionEnvironments(updated);
    formikRef.current.setFieldValue('linksAndTags.nonProductionEnvironments', updated);
  };

  const handleUpdateField = useCallback((id: string, field: keyof NonProductionEnvironmentsEntity, value: string) => {
    setNonProductionEnvironments((prev) => {
      const updated = prev.map((env) => (env.id === id ? { ...env, [field]: value } : env));
      formikRef.current.setFieldValue('linksAndTags.nonProductionEnvironments', updated);
      return updated;
    });
  }, []);

  const addRow = () => {
    syncToFormik([...nonProductionEnvironments, { ...EMPTY_ENV, id: uuidv4() }]);
  };

  const removeSelected = () => {
    const selectedIds = new Set(selectedItems.map((item) => item.id));
    syncToFormik(nonProductionEnvironments.filter((env) => !selectedIds.has(env.id)));
    setSelectedItems([]);
  };

  const columnDefinitions: TableProps.ColumnDefinition<NonProductionEnvironmentsEntity>[] = [
    {
      id: 'displayText',
      header: 'Display Name',
      width: 150,
      cell: (item) => <DisplayNameCell item={item} onUpdate={handleUpdateField} />
    },
    {
      id: 'link',
      header: 'Link',
      cell: (item) => <LinkCell item={item} onUpdate={handleUpdateField} showEmbedWarning={showEmbedWarnings} />
    }
  ];

  const onConfirm = (info: ShowModalInfo) => {
    if (info.eventContentType === 'DELETE_NON_PROD') {
      removeSelected();
    }
    setShowModalInfo({ showModal: false, eventHeader: '', eventContentType: '' });
  };

  const onCancel = () => {
    setShowModalInfo({ showModal: false, eventHeader: '', eventContentType: '' });
  };

  return (
    <>
      <ConfirmationModal showModalInfo={showModalInfo} onConfirm={onConfirm} onCancel={onCancel} />
      <Table
        variant="embedded"
        columnDefinitions={columnDefinitions}
        items={nonProductionEnvironments}
        trackBy="id"
        selectionType="multi"
        selectedItems={selectedItems}
        onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No Environments</b>
            <Box variant="p" color="inherit">
              Click "Add Environment" to add a non-production link.
            </Box>
          </Box>
        }
        header={
          <Header
            variant="h3"
            description="Add links to non-production environments such as alpha, beta, or gamma"
            counter={nonProductionEnvironments.length > 0 ? `(${nonProductionEnvironments.length})` : undefined}
            actions={
              <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                <Checkbox
                  onChange={({ detail }) => {
                    formik.setFieldValue('linksAndTags.hasNonProductionEnvironments', detail.checked);
                    if (!detail.checked) {
                      setSelectedItems([]);
                    }
                  }}
                  checked={formik.values.linksAndTags?.hasNonProductionEnvironments || false}
                >
                  Enable
                </Checkbox>
                {formik.values.linksAndTags?.hasNonProductionEnvironments && (
                  <>
                    <Button formAction="none" onClick={addRow}>
                      Add Environment
                    </Button>
                    <Button
                      formAction="none"
                      disabled={selectedItems.length === 0}
                      onClick={() => {
                        setShowModalInfo({
                          showModal: true,
                          eventHeader: 'Delete Non-Production Environments',
                          eventContentType: 'DELETE_NON_PROD'
                        });
                      }}
                    >
                      {`Delete (${selectedItems.length}) Selected`}
                    </Button>
                  </>
                )}
              </SpaceBetween>
            }
          >
            Non-Production Environments
          </Header>
        }
      />
    </>
  );
};
